/* ── WORK_TRACK — Rich composer (emoji / sticker / GIF / photo) ─
   Shared by the Chat tab and the in-meeting chat. Reads the
   shared `chatState` (DM chat) and `meet.chatWs` runtimes.
   ─────────────────────────────────────────────────────────── */

import { $, $$, esc, icon, chatState, meet, toast, openImageLightbox, apiUpload } from './core';
import { mentionPickerActive } from './mentions';

let meetReconnectHook: (() => void) | null = null;

export function setMeetChatReconnect(fn: (() => void) | null): void {
  meetReconnectHook = fn;
}

function tryMeetReconnect(): void {
  if (meetReconnectHook) meetReconnectHook();
}

const EMOJI_SET = [
  '😀',
  '😁',
  '😂',
  '🤣',
  '😃',
  '😄',
  '😅',
  '😊',
  '😍',
  '😘',
  '😎',
  '🤓',
  '😜',
  '🤪',
  '😇',
  '🥳',
  '😴',
  '🤗',
  '🤔',
  '🙄',
  '😬',
  '😢',
  '😭',
  '😡',
  '🤯',
  '🥺',
  '👍',
  '👎',
  '👏',
  '🙌',
  '🤝',
  '💪',
  '👋',
  '🤞',
  '✌️',
  '👌',
  '🤘',
  '✊',
  '✅',
  '❌',
  '❤️',
  '🧡',
  '💛',
  '💚',
  '💙',
  '💜',
  '🖤',
  '🤍',
  '🔥',
  '✨',
  '🎉',
  '🎊',
  '🎁',
  '🎂',
  '⚡',
  '☕',
  '🍕',
  '🍔',
  '🌮',
  '🍿',
  '🚀',
  '⚽',
  '🎮',
  '🎵',
  '🎶',
  '💡',
  '📌'
];
const STICKER_SET = ['😍', '😂', '🤣', '😎', '🔥', '🎉', '👏', '🙌', '💪', '😴', '🥺', '😱', '🤯', '❤️', '👍', '🚀'];
const GIF_SET = [
  'https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif',
  'https://media.giphy.com/media/13CoXDiaCcCoyk/giphy.gif',
  'https://media.giphy.com/media/26tPplGWjN0xLybiU/giphy.gif',
  'https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif',
  'https://media.giphy.com/media/3o7TKtnuHOHHUjR38Y/giphy.gif'
];

function openComposerPicker(composerId: string, kind: string): void {
  const picker = $<HTMLElement>(`[data-cmp-picker="${composerId}"]`);
  if (picker) picker.dataset.kind = kind;
  const opened = picker && !picker.classList.contains('hidden');
  closeComposerPickers();
  if (opened || !picker) return;
  if (kind === 'emoji') {
    picker.innerHTML = `<div class="cmp-grid">${EMOJI_SET.map(e => `<button type="button" class="cmp-cell" data-send="emoji" data-body="${esc(e)}">${e}</button>`).join('')}</div>`;
    picker.classList.remove('hidden');
  } else if (kind === 'sticker') {
    picker.innerHTML = `<div class="cmp-grid stickers">${STICKER_SET.map(e => `<button type="button" class="cmp-cell big" data-send="sticker" data-body="${esc(e)}">${e}</button>`).join('')}</div>`;
    picker.classList.remove('hidden');
  } else if (kind === 'gif') {
    picker.innerHTML = `
      <div class="cmp-grid gifs">${GIF_SET.map(u => `<button type="button" class="cmp-cell" data-send="gif" data-body="${esc(u)}"><img src="${esc(u)}" alt="GIF" loading="lazy"></button>`).join('')}</div>
      <form class="cmp-url-form"><input placeholder="Or paste a GIF / image URL…" maxlength="2000"><button class="btn btn-sm btn-primary">Add</button></form>`;
    picker.classList.remove('hidden');
  } else if (kind === 'photo') {
    const file = $<HTMLInputElement>(`[data-cmp-file="${composerId}"]`);
    if (file) file.click();
  } else if (kind === 'document') {
    const file = $<HTMLInputElement>(`[data-cmp-doc="${composerId}"]`);
    if (file) file.click();
  }
}

function closeComposerPickers(): void {
  $$<HTMLElement>('.cmp-picker').forEach(p => {
    p.classList.add('hidden');
    p.innerHTML = '';
    ['data-kind', 'data-url', 'data-doc', 'data-composer'].forEach(a => p.removeAttribute(a));
  });
}

function showPendingPhoto(composerId: string, url: string): void {
  const picker = $<HTMLElement>(`[data-cmp-picker="${composerId}"]`);
  if (!picker) return;
  picker.dataset.composer = composerId;
  picker.dataset.url = url;
  picker.innerHTML = `
    <div class="cmp-photo-preview">
      <img src="${esc(url)}" alt="Preview">
      <input type="text" placeholder="Add a message (optional)…" maxlength="2000" autocomplete="off">
      <div class="cmp-photo-actions">
        <button type="button" class="btn btn-ghost btn-sm" data-cmp-cancel>Cancel</button>
        <button type="button" class="btn btn-primary btn-sm" data-cmp-send>Send</button>
      </div>
    </div>`;
  picker.classList.remove('hidden');
  const input = picker.querySelector<HTMLInputElement>('.cmp-photo-preview input');
  if (input) input.focus();
}

function sendPendingPhoto(picker: HTMLElement | null): void {
  if (!picker) return;
  const composerId = picker.dataset.composer || 'chat';
  const url = picker.dataset.url || '';
  const captionEl = picker.querySelector<HTMLInputElement>('.cmp-photo-preview input');
  const caption = captionEl ? captionEl.value.trim() : '';
  if (!url) {
    closeComposerPickers();
    return;
  }
  const isMeet = composerId === 'meet';
  const ws = isMeet ? meet.chatWs : chatState.ws;
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    if (isMeet) tryMeetReconnect();
    toast('Not connected', true);
    return;
  }
  if (caption) sendRichChat('text', caption, composerId);
  sendRichChat('image', url, composerId);
  closeComposerPickers();
}

// ── Document sharing ───────────────────────────────────────
// Files are uploaded to R2 (`POST /api/chat/files`, 20 MB max) and the
// chat message carries only a small `{ n, t, s, u }` metadata payload,
// so receivers get a file card with name/size and a download link.
const MAX_DOC_BYTES = 20 * 1024 * 1024;

interface PendingDoc {
  n: string;
  t: string;
  s: number;
  u?: string; // R2-backed URL once the upload completes
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function showPendingDoc(composerId: string, doc: PendingDoc, uploading: boolean): void {
  const picker = $<HTMLElement>(`[data-cmp-picker="${composerId}"]`);
  if (!picker) return;
  picker.dataset.composer = composerId;
  picker.dataset.doc = JSON.stringify(doc);
  picker.innerHTML = `
    <div class="cmp-photo-preview cmp-doc-preview">
      <div class="cmp-doc-row">
        ${icon('fileText', 22)}
        <div class="cmp-doc-meta">
          <strong>${esc(doc.n)}</strong>
          <span class="cmp-doc-status">${uploading ? 'Uploading…' : formatBytes(doc.s)}</span>
        </div>
      </div>
      <input type="text" placeholder="Add a message (optional)…" maxlength="2000" autocomplete="off">
      <div class="cmp-photo-actions">
        <button type="button" class="btn btn-ghost btn-sm" data-cmp-cancel>Cancel</button>
        <button type="button" class="btn btn-primary btn-sm" data-cmp-send${uploading ? ' disabled' : ''}>Send</button>
      </div>
    </div>`;
  picker.classList.remove('hidden');
  if (!uploading) {
    const input = picker.querySelector<HTMLInputElement>('.cmp-photo-preview input');
    if (input) input.focus();
  }
}

function sendPendingDoc(picker: HTMLElement | null): void {
  if (!picker) return;
  const composerId = picker.dataset.composer || 'chat';
  const raw = picker.dataset.doc || '';
  let doc: PendingDoc | null = null;
  try {
    doc = JSON.parse(raw);
  } catch {
    /* fall through */
  }
  if (!doc) {
    closeComposerPickers();
    return;
  }
  if (!doc.u) {
    toast('Still uploading — one moment…', true);
    return;
  }
  const captionEl = picker.querySelector<HTMLInputElement>('.cmp-photo-preview input');
  const caption = captionEl ? captionEl.value.trim() : '';
  const isMeet = composerId === 'meet';
  const ws = isMeet ? meet.chatWs : chatState.ws;
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    if (isMeet) tryMeetReconnect();
    toast('Not connected', true);
    return;
  }
  if (caption) sendRichChat('text', caption, composerId);
  sendRichChat('document', raw, composerId);
  closeComposerPickers();
}

function sendPendingAttachment(picker: HTMLElement | null): void {
  if (!picker) return;
  if (picker.dataset.doc) sendPendingDoc(picker);
  else sendPendingPhoto(picker);
}

export function setChatPendingImage(url: string | null): void {
  chatState.pendingImage = url;
  const slot = $('#chatPhotoSlot');
  if (!slot) return;
  if (url) {
    slot.innerHTML = `<div class="chat-photo-thumb"><img src="${esc(url)}" alt="Preview"><button type="button" class="chat-photo-x" title="Remove photo">${icon('x', 12)}</button></div>`;
    const x = slot.querySelector<HTMLButtonElement>('.chat-photo-x');
    if (x) x.addEventListener('click', () => setChatPendingImage(null));
    slot.classList.remove('hidden');
    const inp = $<HTMLTextAreaElement>('#chatInput');
    if (inp) inp.focus();
  } else {
    slot.innerHTML = '';
    slot.classList.add('hidden');
  }
}

function sendRichChat(kind: string, body: string, composerId: string): void {
  const isMeet = composerId === 'meet';
  const ws = isMeet ? meet.chatWs : chatState.ws;
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    if (isMeet) tryMeetReconnect();
    toast('Not connected', true);
    return;
  }
  ws.send(JSON.stringify({ type: 'message', body, kind }));
  const input = isMeet ? $<HTMLTextAreaElement>('#meetChatInput') : $<HTMLTextAreaElement>('#chatInput');
  if (input) input.focus();
}

function readFileAsDataURL(file: File, maxDim: number, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Not an image'));
      img.onload = () => {
        let w = img.width;
        let h = img.height;
        if (w > maxDim || h > maxDim) {
          const scale = Math.min(maxDim / w, maxDim / h);
          w = Math.max(1, Math.round(w * scale));
          h = Math.max(1, Math.round(h * scale));
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export function sendComposerMessage(composerId: string): void {
  const isMeet = composerId === 'meet';
  const input = isMeet ? $<HTMLTextAreaElement>('#meetChatInput') : $<HTMLTextAreaElement>('#chatInput');
  const ws = isMeet ? meet.chatWs : chatState.ws;
  const body = input.value.trim();
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    if (isMeet) tryMeetReconnect();
    toast('Not connected', true);
    return;
  }
  if (isMeet) {
    if (!body) {
      toast('Type a message first', true);
      return;
    }
    ws.send(JSON.stringify({ type: 'message', body }));
    input.value = '';
    resetChatHeight(input);
    return;
  }
  const imgUrl = chatState.pendingImage;
  if (!body && !imgUrl) {
    toast('Type a message or pick a photo', true);
    return;
  }
  if (body) sendRichChat('text', body, 'chat');
  if (imgUrl) sendRichChat('image', imgUrl, 'chat');
  input.value = '';
  resetChatHeight(input);
  setChatPendingImage(null);
}

const MAX_CHAT_HEIGHT = 160;

function resetChatHeight(input: HTMLTextAreaElement): void {
  input.style.height = '';
  input.style.overflowY = 'hidden';
}

// Enter sends the message; Shift+Enter inserts a new line. The box grows as
// you type (up to a cap) and only then scrolls internally.
export function setupChatTextarea(input: HTMLTextAreaElement, form: HTMLFormElement): void {
  const grow = (): void => {
    input.style.height = 'auto';
    const h = Math.min(input.scrollHeight, MAX_CHAT_HEIGHT);
    input.style.height = `${h}px`;
    input.style.overflowY = input.scrollHeight > MAX_CHAT_HEIGHT ? 'auto' : 'hidden';
  };
  input.addEventListener('input', grow);
  input.addEventListener('focus', grow);
  grow();
  input.addEventListener('keydown', e => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (mentionPickerActive()) return;
    e.preventDefault();
    form.requestSubmit();
  });
}

// ── Global delegated handlers ─────────────────────────────
document.addEventListener('click', e => {
  const target = e.target as HTMLElement;
  const media = target.closest('.chat-media') as HTMLImageElement | null;
  if (media && media.src) {
    openImageLightbox(media.src);
    return;
  }
  const cmpBtn = target.closest<HTMLElement>('[data-cmp]');
  if (cmpBtn) {
    openComposerPicker(cmpBtn.dataset.composer || 'chat', cmpBtn.dataset.cmp || '');
    return;
  }
  const cmpSend = target.closest<HTMLElement>('[data-cmp-send]');
  if (cmpSend) {
    sendPendingAttachment(cmpSend.closest<HTMLElement>('.cmp-picker'));
    return;
  }
  const cmpCancel = target.closest<HTMLElement>('[data-cmp-cancel]');
  if (cmpCancel) {
    closeComposerPickers();
    return;
  }
  const send = target.closest<HTMLElement>('[data-send]');
  if (send) {
    const composer = send.closest<HTMLElement>('.composer');
    sendRichChat(
      send.dataset.send || 'text',
      send.dataset.body || '',
      composer ? composer.dataset.composerId || 'chat' : 'chat'
    );
    closeComposerPickers();
  }
});

document.addEventListener('submit', e => {
  const form = (e.target as HTMLElement).closest<HTMLFormElement>('.cmp-url-form');
  if (!form) return;
  e.preventDefault();
  const input = form.querySelector<HTMLInputElement>('input');
  const url = ((input && input.value) || '').trim();
  if (!url) return;
  const composer = form.closest<HTMLElement>('.composer');
  sendRichChat('gif', url, composer ? composer.dataset.composerId || 'chat' : 'chat');
  closeComposerPickers();
});

document.addEventListener('change', e => {
  const file = (e.target as HTMLElement).closest<HTMLInputElement>('[data-cmp-file]');
  if (!file || !file.files || !file.files[0]) return;
  const composerId = file.dataset.cmpFile || 'chat';
  const f = file.files[0];
  file.value = '';
  file.classList.add('hidden');
  if (!f.type || !f.type.startsWith('image/')) {
    toast('Please pick an image file', true);
    return;
  }
  readFileAsDataURL(f, 1920, 0.85)
    .then(url => {
      if (composerId === 'chat') setChatPendingImage(url);
      else showPendingPhoto(composerId, url);
    })
    .catch(() => toast('Could not read that image', true));
});

document.addEventListener('change', e => {
  const file = (e.target as HTMLElement).closest<HTMLInputElement>('[data-cmp-doc]');
  if (!file || !file.files || !file.files[0]) return;
  const composerId = file.dataset.cmpDoc || 'chat';
  const f = file.files[0];
  file.value = '';
  file.classList.add('hidden');
  if (f.size > MAX_DOC_BYTES) {
    toast('File too large — max 20 MB', true);
    return;
  }
  showPendingDoc(composerId, { n: f.name || 'Document', t: f.type || 'application/octet-stream', s: f.size }, true);
  apiUpload<{ url: string }>('/api/chat/files', f)
    .then(res => {
      const picker = $<HTMLElement>(`[data-cmp-picker="${composerId}"]`);
      let raw: PendingDoc | null = null;
      try {
        raw = picker && picker.dataset.doc ? JSON.parse(picker.dataset.doc) : null;
      } catch {
        raw = null;
      }
      // The user may have cancelled while the upload was in flight.
      if (!picker || !raw || picker.classList.contains('hidden')) return;
      raw.u = res.url;
      picker.dataset.doc = JSON.stringify(raw);
      const status = picker.querySelector<HTMLElement>('.cmp-doc-status');
      if (status) status.textContent = formatBytes(raw.s);
      const send = picker.querySelector<HTMLButtonElement>('[data-cmp-send]');
      if (send) send.disabled = false;
      const input = picker.querySelector<HTMLInputElement>('.cmp-photo-preview input');
      if (input) input.focus();
    })
    .catch(err => {
      toast(err instanceof Error ? err.message : 'Upload failed', true);
      closeComposerPickers();
    });
});

document.addEventListener('keydown', e => {
  const cap = (e.target as HTMLElement).closest<HTMLInputElement>('.cmp-photo-preview input');
  if (cap && e.key === 'Enter') {
    e.preventDefault();
    const picker = cap.closest<HTMLElement>('.cmp-picker');
    if (picker) sendPendingAttachment(picker);
  }
});
